import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  Table, Thead, Tbody, Tr, Th, Td, Divider, Select, NumberInput, NumberInputField,
  IconButton,
} from '@chakra-ui/react';
import { FiPlay, FiSquare, FiClock, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// ── Inline row editor for a single session ───────────────────────────────────
function SessionEditRow({ session, subTasks, onSave, onCancel }) {
  const toLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [form, setForm] = useState({
    title: session.title || '',
    description: session.description || '',
    start_time: toLocal(session.start_time),
    end_time: toLocal(session.end_time),
    sub_task_id: session.sub_task_id || '',
    progress_after: session.progress_after ?? '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Tr bg="yellow.50">
      <Td colSpan={7} p={2}>
        <VStack spacing={2} align="stretch">
          <HStack spacing={2}>
            <FormControl flex={2}>
              <FormLabel fontSize="9px" mb={0}>Title</FormLabel>
              <Input size="xs" value={form.title} onChange={e => set('title', e.target.value)} />
            </FormControl>
            <FormControl flex={2}>
              <FormLabel fontSize="9px" mb={0}>Sub-task</FormLabel>
              <Select size="xs" value={form.sub_task_id} onChange={e => set('sub_task_id', e.target.value)}>
                <option value="">— None —</option>
                {subTasks.map(st => <option key={st.id} value={st.id}>{st.title}</option>)}
              </Select>
            </FormControl>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>Progress %</FormLabel>
              <Input size="xs" type="number" min={0} max={100} value={form.progress_after}
                onChange={e => set('progress_after', e.target.value)} placeholder="—" />
            </FormControl>
          </HStack>
          <HStack spacing={2}>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>Start time</FormLabel>
              <Input size="xs" type="datetime-local" value={form.start_time}
                onChange={e => set('start_time', e.target.value)} />
            </FormControl>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>End time <Text as="span" color="orange.500">(clears active if set)</Text></FormLabel>
              <Input size="xs" type="datetime-local" value={form.end_time}
                onChange={e => set('end_time', e.target.value)} />
            </FormControl>
          </HStack>
          <HStack justify="flex-end" spacing={2}>
            <Button size="xs" variant="ghost" leftIcon={<FiX size={11} />} onClick={onCancel}>Cancel</Button>
            <Button size="xs" colorScheme="teal" leftIcon={<FiCheck size={11} />}
              onClick={() => onSave({
                title: form.title || null,
                description: form.description || null,
                start_time: form.start_time ? new Date(form.start_time).toISOString() : undefined,
                end_time: form.end_time ? new Date(form.end_time).toISOString() : undefined,
                sub_task_id: form.sub_task_id || null, // send UUID; backend resolves
                progress_after: form.progress_after !== '' ? parseInt(form.progress_after) : null,
              })}>
              Save
            </Button>
          </HStack>
        </VStack>
      </Td>
    </Tr>
  );
}

// ── Manual add row ────────────────────────────────────────────────────────────
function ManualAddRow({ taskId, projectId, subTasks, onSaved, onCancel }) {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const oneHourAgo = new Date(now.getTime() - 3600000);

  const [form, setForm] = useState({
    title: '', description: '', start_time: toLocal(oneHourAgo), end_time: toLocal(now),
    sub_task_id: '', progress_after: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.start_time || !form.end_time) {
      toast({ title: 'Start and end time required', status: 'warning', duration: 2000 }); return;
    }
    if (new Date(form.end_time) <= new Date(form.start_time)) {
      toast({ title: 'End time must be after start time', status: 'warning', duration: 2000 }); return;
    }
    setSaving(true);
    try {
      await api.post('/work-sessions/manual', {
        task_id: taskId, project_id: projectId,
        title: form.title || null, description: form.description || null,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        sub_task_id: form.sub_task_id || null, // send UUID; backend resolves
        progress_after: form.progress_after !== '' ? parseInt(form.progress_after) : null,
      });
      toast({ title: 'Session added', status: 'success', duration: 1500 });
      onSaved();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  return (
    <Tr bg="green.50">
      <Td colSpan={7} p={2}>
        <Text fontSize="9px" fontWeight="700" color="green.700" textTransform="uppercase" mb={2}>
          Add Manual Session
        </Text>
        <VStack spacing={2} align="stretch">
          <HStack spacing={2}>
            <FormControl flex={2}>
              <FormLabel fontSize="9px" mb={0}>Title</FormLabel>
              <Input size="xs" value={form.title} placeholder="What was worked on?"
                onChange={e => set('title', e.target.value)} />
            </FormControl>
            <FormControl flex={2}>
              <FormLabel fontSize="9px" mb={0}>Sub-task</FormLabel>
              <Select size="xs" value={form.sub_task_id} onChange={e => set('sub_task_id', e.target.value)}>
                <option value="">— None —</option>
                {subTasks.map(st => <option key={st.id} value={st.id}>{st.title}</option>)}
              </Select>
            </FormControl>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>Progress %</FormLabel>
              <Input size="xs" type="number" min={0} max={100} value={form.progress_after}
                onChange={e => set('progress_after', e.target.value)} placeholder="—" />
            </FormControl>
          </HStack>
          <HStack spacing={2}>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>Clock In *</FormLabel>
              <Input size="xs" type="datetime-local" value={form.start_time}
                onChange={e => set('start_time', e.target.value)} />
            </FormControl>
            <FormControl flex={1}>
              <FormLabel fontSize="9px" mb={0}>Clock Out *</FormLabel>
              <Input size="xs" type="datetime-local" value={form.end_time}
                onChange={e => set('end_time', e.target.value)} />
            </FormControl>
          </HStack>
          <HStack justify="flex-end" spacing={2}>
            <Button size="xs" variant="ghost" leftIcon={<FiX size={11} />} onClick={onCancel}>Cancel</Button>
            <Button size="xs" colorScheme="green" leftIcon={<FiCheck size={11} />}
              onClick={handleSave} isLoading={saving}>
              Add Session
            </Button>
          </HStack>
        </VStack>
      </Td>
    </Tr>
  );
}

// ── Main WorkSessionModal ─────────────────────────────────────────────────────
export default function WorkSessionModal({ taskId, taskTitle, projectId, isOpen, onClose, onRefresh }) {
  const { user } = useAuth();
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [subTasks, setSubTasks] = useState([]);
  const [allSubTasks, setAllSubTasks] = useState([]); // including completed, for edit dropdowns
  const [selectedSubTaskId, setSelectedSubTaskId] = useState('');
  const [subTaskMode, setSubTaskMode] = useState('existing');
  const [newSubTask, setNewSubTask] = useState({ title: '', description: '', percentage_contribution: 10, projected_hours: '' });
  const [elapsed, setElapsed] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [progressAfter, setProgressAfter] = useState(0);
  const [showStop, setShowStop] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const timerRef = useRef(null);
  const toast = useToast();

  const fetchSessions = async () => {
    if (!taskId) return;
    try { const { data } = await api.get(`/work-sessions?task_id=${taskId}`); setSessions(data); } catch(e) {}
  };

  const fetchSubTasks = async () => {
    if (!taskId) return;
    try {
      const { data } = await api.get(`/sub-tasks?task_id=${taskId}`);
      setAllSubTasks(data);
      setSubTasks(data.filter(s => !s.is_completed));
    } catch(e) { setSubTasks([]); setAllSubTasks([]); }
  };

  const fetchActive = async () => {
    try {
      const { data } = await api.get('/work-sessions/active');
      // Both data.task_id (from rewriteRowFull in Phase 3) and taskId prop are now UUIDs.
      // Direct equality comparison is correct and sufficient.
      if (data && data.task_id === taskId) {
        setActiveSession(data);
        setElapsed(Math.floor((Date.now() - new Date(data.start_time).getTime()) / 1000));
      } else { setActiveSession(null); setElapsed(0); }
    } catch(e) {}
  };

  useEffect(() => {
    if (isOpen && taskId) {
      fetchSessions(); fetchSubTasks(); fetchActive();
      setShowStop(false); setTitle(''); setDescription('');
      setSelectedSubTaskId(''); setSubTaskMode('existing');
      setNewSubTask({ title: '', description: '', percentage_contribution: 10, projected_hours: '' });
      setEditingId(null); setShowManualAdd(false);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isOpen, taskId]); // eslint-disable-line

  useEffect(() => {
    if (activeSession) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - new Date(activeSession.start_time).getTime()) / 1000));
      }, 1000);
    } else { if (timerRef.current) clearInterval(timerRef.current); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeSession]);

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); const s = secs % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const handleStart = async () => {
    let subTaskId = null;
    if (subTaskMode === 'existing') {
      if (subTasks.length > 0 && !selectedSubTaskId) {
        toast({ title: 'Select a sub-task or choose "New sub-task" / "Task only"', status: 'warning', duration: 2000 });
        return;
      }
      subTaskId = selectedSubTaskId || null; // send UUID as-is; backend resolves via resolveBodyId
    } else if (subTaskMode === 'new') {
      if (!newSubTask.title.trim()) { toast({ title: 'Sub-task title required', status: 'warning', duration: 2000 }); return; }
      try {
        const { data: created } = await api.post('/sub-tasks', {
          task_id: taskId, title: newSubTask.title.trim(), description: newSubTask.description || null,
          percentage_contribution: parseFloat(newSubTask.percentage_contribution) || 10,
          projected_hours: newSubTask.projected_hours ? parseFloat(newSubTask.projected_hours) : null,
          sort_order: subTasks.length,
        });
        subTaskId = created.id;
        fetchSubTasks();
        toast({ title: `Sub-task "${created.title}" created`, status: 'success', duration: 1500 });
      } catch(e) { toast({ title: 'Failed to create sub-task', status: 'error', duration: 2000 }); return; }
    }

    try {
      const sessionTitle = title ||
        (subTaskId && subTaskMode === 'new' ? newSubTask.title :
         subTaskId ? subTasks.find(s => s.id === subTaskId)?.title : null) || taskTitle;
      const { data } = await api.post('/work-sessions', {
        task_id: taskId, project_id: projectId, title: sessionTitle, description, sub_task_id: subTaskId,
      });
      setActiveSession(data); setElapsed(0);
      toast({ title: 'Timer started', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed to start', status: 'error', duration: 2000 }); }
  };

  const handleStop = async () => {
    if (!activeSession) return;
    try {
      await api.put(`/work-sessions/${activeSession.id}/stop`, { progress_after: progressAfter > 0 ? progressAfter : null });
      toast({ title: 'Session logged', status: 'success', duration: 1500 });
      setActiveSession(null); setElapsed(0); setShowStop(false);
      await fetchSessions(); if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleEdit = async (id, payload) => {
    try {
      await api.put(`/work-sessions/${id}`, payload);
      setEditingId(null);
      await fetchSessions(); await fetchActive();
      toast({ title: 'Session updated', status: 'success', duration: 1500 });
      if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed to update', status: 'error', duration: 2000 }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this work session permanently?')) return;
    try {
      await api.delete(`/work-sessions/${id}`);
      toast({ title: 'Session deleted', status: 'info', duration: 1500 });
      await fetchSessions(); await fetchActive();
      if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed to delete', status: 'error', duration: 2000 }); }
  };

  const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const statusColor = (s) => ({ active: 'green', completed: 'blue', cancelled: 'gray' }[s] || 'gray');

  // Drag-to-move
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: null, y: null });

  const onDragMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const el = dragRef.current?.closest('[role="dialog"]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const onMove = (ev) => {
      setPosition({ x: ev.clientX - offsetRef.current.x, y: ev.clientY - offsetRef.current.y });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh"
        style={position.x !== null ? { position: 'fixed', left: position.x, top: position.y, margin: 0 } : {}}>
        <ModalHeader fontSize="md" ref={dragRef} onMouseDown={onDragMouseDown}
          cursor="grab" userSelect="none" _active={{ cursor: 'grabbing' }}>
          <Flex justify="space-between" pr={8} align="center">
            <HStack><FiClock /><Text>Work Sessions</Text></HStack>
            <Text fontSize="11px" color="gray.500">{taskTitle}</Text>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">

            {/* ── Active timer ── */}
            {activeSession ? (
              <Box bg="green.50" border="2px" borderColor="green.300" p={4} borderRadius="md" textAlign="center">
                <Text fontSize="10px" color="green.600" fontWeight="600" textTransform="uppercase" mb={1}>Timer Running</Text>
                <Text fontSize="36px" fontWeight="800" fontFamily="mono" color="green.700">{formatElapsed(elapsed)}</Text>
                <Text fontSize="11px" color="gray.500" mt={1}>{activeSession.title || 'Working...'}</Text>
                {activeSession.sub_task_title && (
                  <Badge colorScheme="purple" fontSize="9px" mt={1}>Sub-task: {activeSession.sub_task_title}</Badge>
                )}
                <HStack justify="center" mt={3} spacing={2}>
                  {!showStop ? (
                    <>
                      <Button size="sm" colorScheme="red" leftIcon={<FiSquare />} onClick={() => setShowStop(true)}>
                        Stop Timer
                      </Button>
                      <Button size="xs" variant="outline" colorScheme="orange"
                        onClick={() => setEditingId(activeSession.id)}>
                        Edit / Fix times
                      </Button>
                    </>
                  ) : (
                    <Box bg="white" p={3} borderRadius="md" textAlign="left" w="100%">
                      <Text fontSize="11px" fontWeight="600" mb={2}>Log this session:</Text>
                      <FormControl mb={2}>
                        <FormLabel fontSize="xs" mb={0.5}>Progress after this work (%)</FormLabel>
                        <Flex align="center" gap={2}>
                          <Slider min={0} max={100} step={5} value={progressAfter}
                            onChange={v => setProgressAfter(v)} colorScheme="green" flex={1}>
                            <SliderTrack h="6px" borderRadius="full"><SliderFilledTrack /></SliderTrack>
                            <SliderThumb boxSize={4} />
                          </Slider>
                          <Badge colorScheme={progressAfter >= 100 ? 'green' : progressAfter > 0 ? 'blue' : 'gray'}
                            fontSize="md" px={2}>{progressAfter}%</Badge>
                        </Flex>
                      </FormControl>
                      <HStack justify="flex-end">
                        <Button size="sm" variant="ghost" onClick={() => setShowStop(false)}>Cancel</Button>
                        <Button size="sm" colorScheme="red" leftIcon={<FiSquare />} onClick={handleStop}>Stop &amp; Log</Button>
                      </HStack>
                    </Box>
                  )}
                </HStack>
              </Box>
            ) : (
              /* ── Start form ── */
              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="11px" fontWeight="600" mb={3}>Start a work session</Text>
                <VStack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Attribute to sub-task</FormLabel>
                    <HStack spacing={2}>
                      {subTasks.length > 0 && (
                        <Button size="xs" colorScheme={subTaskMode === 'existing' ? 'blue' : 'gray'}
                          variant={subTaskMode === 'existing' ? 'solid' : 'outline'}
                          onClick={() => setSubTaskMode('existing')}>Existing</Button>
                      )}
                      <Button size="xs" colorScheme={subTaskMode === 'new' ? 'teal' : 'gray'}
                        variant={subTaskMode === 'new' ? 'solid' : 'outline'}
                        leftIcon={<FiPlus size={11} />}
                        onClick={() => setSubTaskMode('new')}>New sub-task</Button>
                      <Button size="xs" colorScheme={subTaskMode === 'none' ? 'orange' : 'gray'}
                        variant={subTaskMode === 'none' ? 'solid' : 'outline'}
                        onClick={() => setSubTaskMode('none')}>Task only</Button>
                    </HStack>
                  </FormControl>

                  {subTaskMode === 'existing' && subTasks.length > 0 && (
                    <FormControl>
                      <FormLabel fontSize="xs" mb={0.5}>Select sub-task *</FormLabel>
                      <Select size="sm" value={selectedSubTaskId} placeholder="Select a sub-task..."
                        onChange={e => { setSelectedSubTaskId(e.target.value); const st = subTasks.find(s => s.id === e.target.value); if (st && !title) setTitle(st.title); }}>
                        {subTasks.map(st => <option key={st.id} value={st.id}>{st.title} ({parseFloat(st.percentage_contribution||0).toFixed(0)}%)</option>)}
                      </Select>
                    </FormControl>
                  )}

                  {subTaskMode === 'new' && (
                    <Box w="100%" bg="teal.50" p={3} borderRadius="md" border="1px" borderColor="teal.200">
                      <Text fontSize="9px" fontWeight="700" color="teal.700" textTransform="uppercase" mb={2}>New Sub-task</Text>
                      <VStack spacing={2}>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={0.5}>Title *</FormLabel>
                          <Input size="sm" value={newSubTask.title} placeholder="What needs doing?"
                            onChange={e => setNewSubTask(p => ({ ...p, title: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                          <Textarea size="sm" rows={2} value={newSubTask.description} placeholder="Optional details..."
                            onChange={e => setNewSubTask(p => ({ ...p, description: e.target.value }))} />
                        </FormControl>
                        <HStack w="100%" spacing={2}>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Contribution %</FormLabel>
                            <NumberInput size="sm" min={0} max={100} value={newSubTask.percentage_contribution}
                              onChange={v => setNewSubTask(p => ({ ...p, percentage_contribution: v }))}>
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Projected hours</FormLabel>
                            <NumberInput size="sm" min={0} step={0.5} value={newSubTask.projected_hours}
                              onChange={v => setNewSubTask(p => ({ ...p, projected_hours: v }))}>
                              <NumberInputField placeholder="e.g. 2.5" />
                            </NumberInput>
                          </FormControl>
                        </HStack>
                      </VStack>
                    </Box>
                  )}

                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Session title <Text as="span" color="gray.400">(optional)</Text></FormLabel>
                    <Input size="sm" value={title}
                      placeholder={subTaskMode === 'new' ? newSubTask.title || taskTitle : subTaskMode === 'existing' && selectedSubTaskId ? allSubTasks.find(s=>s.id===selectedSubTaskId)?.title || taskTitle : taskTitle}
                      onChange={e => setTitle(e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Notes</FormLabel>
                    <Textarea size="sm" rows={2} value={description} placeholder="What are you working on?"
                      onChange={e => setDescription(e.target.value)} />
                  </FormControl>
                  <Button size="sm" colorScheme="green" leftIcon={<FiPlay />} onClick={handleStart} alignSelf="flex-end">
                    Clock On
                  </Button>
                </VStack>
              </Box>
            )}

            {/* ── Session history ── */}
            <Divider />
            <Flex justify="space-between" align="center">
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">
                Session History ({sessions.length})
              </Text>
              <Button size="xs" leftIcon={<FiPlus size={11} />} colorScheme="green" variant="outline"
                onClick={() => { setShowManualAdd(true); setEditingId(null); }}>
                Add Manual Session
              </Button>
            </Flex>

            {(sessions.length > 0 || showManualAdd) && (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th px={2} fontSize="9px">Date</Th>
                      <Th px={2} fontSize="9px">Title</Th>
                      <Th px={2} fontSize="9px">Sub-Task</Th>
                      <Th px={2} fontSize="9px">Duration</Th>
                      <Th px={2} fontSize="9px">Progress</Th>
                      <Th px={2} fontSize="9px">User</Th>
                      <Th px={1} w="60px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* Manual add row at top */}
                    {showManualAdd && (
                      <ManualAddRow taskId={taskId} projectId={projectId} subTasks={allSubTasks}
                        onSaved={() => { setShowManualAdd(false); fetchSessions(); if(onRefresh) onRefresh(); }}
                        onCancel={() => setShowManualAdd(false)} />
                    )}

                    {sessions.map(s => (
                      editingId === s.id ? (
                        <SessionEditRow key={s.id} session={s} subTasks={allSubTasks}
                          onSave={(payload) => handleEdit(s.id, payload)}
                          onCancel={() => setEditingId(null)} />
                      ) : (
                        <Tr key={s.id} _hover={{ bg: 'gray.50' }}
                          bg={s.status === 'active' ? 'green.50' : s.status === 'cancelled' ? 'red.50' : 'white'}
                          opacity={s.status === 'cancelled' ? 0.6 : 1}>
                          <Td px={2} whiteSpace="nowrap" fontSize="11px">{fmtDt(s.start_time)}</Td>
                          <Td px={2} maxW="120px" isTruncated fontSize="11px">{s.title || '—'}</Td>
                          <Td px={2} maxW="180px" isTruncated fontSize="11px" title={s.sub_task_title || ''}>{s.sub_task_title || '—'}</Td>
                          <Td px={2}>
                            {s.status === 'active' ? (
                              <Badge colorScheme="green" fontSize="9px">Active</Badge>
                            ) : s.status === 'cancelled' ? (
                              <Badge colorScheme="gray" fontSize="9px">Cancelled</Badge>
                            ) : (
                              <Text fontFamily="mono" fontSize="11px">
                                {s.duration_minutes ? `${Math.floor(s.duration_minutes/60)}h ${s.duration_minutes%60}m` : '—'}
                              </Text>
                            )}
                          </Td>
                          <Td px={2}>
                            {s.progress_after !== null && s.progress_after !== undefined
                              ? <Badge colorScheme="blue" fontSize="9px">{s.progress_after}%</Badge>
                              : '—'}
                          </Td>
                          <Td px={2} fontSize="11px">{s.user_name || '—'}</Td>
                          <Td px={1}>
                            <HStack spacing={0}>
                              <IconButton icon={<FiEdit2 size={12} />} size="xs" variant="ghost" colorScheme="blue"
                                aria-label="Edit" onClick={() => { setEditingId(s.id); setShowManualAdd(false); }} />
                              <IconButton icon={<FiTrash2 size={12} />} size="xs" variant="ghost" colorScheme="red"
                                aria-label="Delete" onClick={() => handleDelete(s.id)} />
                            </HStack>
                          </Td>
                        </Tr>
                      )
                    ))}

                    {sessions.length === 0 && !showManualAdd && (
                      <Tr><Td colSpan={7} textAlign="center" py={4} fontSize="12px" color="gray.400">
                        No sessions yet.
                      </Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
